/**
 * Canonical reason codes for magic link callback failures.
 *
 * The backend returns user-readable detail strings for each failure case
 * (see `supplyco_api/src/auth/router.py:magic_link_callback`). We map those
 * detail strings to a small set of stable codes so that the frontend can
 * (a) keep messaging under client-side control and
 * (b) avoid leaking raw backend copy through URLs.
 */
const MAGIC_LINK_ERROR_REASONS = [
  'used',
  'expired',
  'invalid',
  'no_account',
  'unknown',
] as const

export type MagicLinkErrorReason = typeof MAGIC_LINK_ERROR_REASONS[number]

export function isMagicLinkErrorReason(value: unknown): value is MagicLinkErrorReason {
  return typeof value === 'string'
    && (MAGIC_LINK_ERROR_REASONS as readonly string[]).includes(value)
}

/**
 * Map a backend `detail` string to a canonical reason code.
 *
 * Falls back to 'unknown' for empty / unrecognized detail strings (e.g. network
 * errors or unexpected 5xx responses).
 */
export function magicLinkErrorReasonFromDetail(detail: string | undefined): MagicLinkErrorReason {
  if (!detail)
    return 'unknown'
  const lower = detail.toLowerCase()
  if (lower.includes('already been used'))
    return 'used'
  if (lower.includes('expired'))
    return 'expired'
  if (lower.includes('no account found'))
    return 'no_account'
  if (lower.includes('invalid magic link') || lower.includes('this magic link is invalid'))
    return 'invalid'
  return 'unknown'
}
